import { isNumeric } from "./number.utils";

export const formatBigintToNumber = (value: bigint, decimals = 18): number => {
  const scale = 10n ** BigInt(decimals);
  const integerPart = value / scale;
  const fractionalPart = value % scale;

  return Number(integerPart) + Number(fractionalPart) / Math.pow(10, decimals);
};

const getPrecisionFromFormat = (format: string) => {
  const match = /\.0+/.exec(format);
  return match ? match[0].length - 1 : 0;
};

const getDynamicPrecision = (value: number): number => {
  return value.toString().split(".")?.[1]?.length || 0;
};

/**
 * Formats a numeric value with various options.
 * @param value - The number or bigint to format
 * @param options - Formatting options. Use precision = -1 for dynamic precision (auto-detect from value).
 *
 * Template wildcards:
 * - `{-}` - Negative sign placeholder (replaced with '-' for negative values)
 * - `{±}` - Sign placeholder (replaced with '-' or '+')
 */
export const formatValue = (
  value?: number | bigint,
  options: Intl.NumberFormatOptions & {
    locales?: string | string[];
    template?: string;
    decimals?: number;
    precision?: number;
  } = {},
) => {
  const {
    style = "decimal",
    locales = "en-US",
    template,
    decimals = 18,
    precision = 2,
    trailingZeroDisplay,
    ...rest
  } = options;

  const numericValue =
    typeof value === "bigint" ? formatBigintToNumber(value, decimals) : value;

  if (!isNumeric(numericValue)) return "-";

  const useDynamicPrecision = precision === -1;

  let intermediatePrecision: number;
  if (template) {
    intermediatePrecision = getPrecisionFromFormat(template);
  } else if (useDynamicPrecision) {
    intermediatePrecision = getDynamicPrecision(numericValue);
  } else {
    intermediatePrecision = precision;
  }

  const finalPrecision =
    trailingZeroDisplay === "stripIfInteger" && numericValue === 0
      ? 0
      : intermediatePrecision;
  const numberPattern = /0\.?0*/;

  const formatOptions: Intl.NumberFormatOptions = {
    style,
    maximumFractionDigits: finalPrecision,
    trailingZeroDisplay,
    ...rest,
  };

  // For dynamic precision, don't force minimum fraction digits to avoid padding with zeros
  if (!useDynamicPrecision) {
    formatOptions.minimumFractionDigits = finalPrecision;
  }

  const formattedValue = new Intl.NumberFormat(locales, formatOptions).format(
    template?.includes("{-}") || template?.includes("{±}")
      ? Math.abs(numericValue)
      : numericValue,
  );

  return (
    template
      ?.replace(numberPattern, formattedValue)
      ?.replace(/{-}/g, numericValue < 0 ? "-" : "")
      ?.replace(/{±}/g, numericValue < 0 ? "-" : "+") ?? formattedValue
  );
};

export const formatPercent = (
  value: number,
  options: Intl.NumberFormatOptions & {
    precision?: number;
    signDisplay?: Intl.NumberFormatOptions["signDisplay"];
  } = {},
) => {
  const { precision = 0, signDisplay = "exceptZero", ...rest } = options;

  return formatValue(value, {
    precision,
    style: "percent",
    signDisplay,
    ...rest,
  });
};

/**
 * Formats a currency value with various options.
 * @param value - The number or bigint to format as currency
 * @param options - Formatting options. Use precision = -1 for dynamic precision (auto-detect from value).
 */
export const formatCurrency = (
  value?: number | bigint,
  options: Intl.NumberFormatOptions & {
    locales?: string | string[];
    template?: string;
    precision?: number;
    decimals?: number;
  } = {},
) => {
  const {
    style = "currency",
    locales = "en-US",
    currency = "USD",
    template,
    precision = 2,
    decimals = 18,
    ...rest
  } = options;

  return formatValue(value, {
    style,
    locales,
    currency,
    template,
    precision,
    decimals,
    ...rest,
  });
};

export const formatCompact = (
  value?: number,
  options: Intl.NumberFormatOptions & {
    locales?: string | string[];
    precision?: number;
    maxPrecision?: number;
    showCurrency?: boolean;
  } = {},
) => {
  const {
    style = "currency",
    locales = "en-US",
    currency = "USD",
    precision = 0,
    showCurrency = false,
    maxPrecision = 4,
    maximumFractionDigits = 3,
    ...rest
  } = options;

  if (value === Infinity) return "∞";

  return formatValue(Number((value ?? 0).toPrecision(maxPrecision)), {
    notation: "compact",
    precision,
    maximumFractionDigits,
    ...(showCurrency && {
      style,
      locales,
      currency,
    }),
    ...rest,
  });
};
